import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { FormattedPrice } from '../FormattedPrice.tsx';

import { useItemPriceData } from '../hooks/useItemPriceData.ts';

import { type ItemPrice_item$key } from './__generated__/ItemPrice_item.graphql.ts';
import { type ItemPrice_displayPrice$key } from './__generated__/ItemPrice_displayPrice.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './ItemPrice.scss';

const itemFragment = graphql`
    fragment ItemPrice_item on Item {
        ...useItemPriceData_item
    }
`;

const displayPriceFragment = graphql`
    fragment ItemPrice_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        ...useItemPriceData_displayPrice
    }
`;

type PriceData = NonNullable<ReturnType<typeof useItemPriceData>>;

const CurrentPrice: FC<{
    currentPrice: PriceData['currentPrice'];
    quantityLabel: PriceData['quantityLabel'];
    currency: string;
}> = ({ currentPrice, currency, quantityLabel }) => {
    if (!currentPrice.amount) {
        return null;
    }

    return (
        <span className={classnames(dibsCss.inlineBlock, dibsCss.mrXxsmall)}>
            <span
                className={classnames(dibsCss.sassyFontBodyMediumHeavy, {
                    [dibsCss.textAlertsecondary]: currentPrice.type === 'WEEKLY_SALE',
                })}
            >
                <FormattedPrice value={currentPrice.amount} currency={currency} />
            </span>
            {quantityLabel && (
                <span
                    className={classnames(
                        dibsCss.inlineBlock,
                        dibsCss.sassyFontBodySmall,
                        dibsCss.alignTextBottom,
                        dibsCss.mlXxsmall,
                        dibsCss.textSatan
                    )}
                >
                    {quantityLabel}
                </span>
            )}
        </span>
    );
};

const CurrentPriceLabelAndPercentageOff: FC<{
    type: PriceData['currentPrice']['type'];
    percentageOff: PriceData['percentageOff'];
}> = ({ type, percentageOff }) => {
    const showPriceLabel = ['NET', 'PRIVATE_OFFER'].includes(type);
    const showPercentageOff = !!percentageOff; // we want to show percentage off only if it's not falsy (including 0)

    if (!showPriceLabel && !showPercentageOff) {
        return null;
    }

    return (
        <span
            className={classnames(dibsCss.sassyFontBodySmall, dibsCss.alignTextBottom, {
                [dibsCss.textAlertsecondary]: type === 'WEEKLY_SALE' && showPercentageOff,
            })}
        >
            <FormattedMessage
                id="ItemPrice.priceLabel"
                defaultMessage="({type, select, NET {Net Price} PRIVATE_OFFER {Private Offer} other {}}{showDivider, select, true { } other {}}{showPercentageOff, select, true {{percentageOff}% Off} other {}})"
                values={{
                    type,
                    showDivider: showPriceLabel && showPercentageOff,
                    showPercentageOff,
                    percentageOff,
                }}
            />
        </span>
    );
};

export const ItemPrice: FC<{
    item?: ItemPrice_item$key | null;
    displayPrice?: ItemPrice_displayPrice$key | null;
    currency: string;
}> = ({ item: itemRef, displayPrice: displayPriceRef, currency }) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);

    const data = useItemPriceData({ item, displayPrice, currency });

    if (!data) {
        return null;
    }

    const { currentPrice, originalPrice, quantityLabel, percentageOff } = data;

    if (currentPrice.type === 'PRICE_UPON_REQUEST') {
        return (
            <div className={classnames(dibsCss.sassyFontBody, dibsCss.textNoir)}>
                <FormattedMessage
                    id="ItemPrice.priceUponRequest"
                    defaultMessage="Price Upon Request"
                />
            </div>
        );
    }

    return (
        <span className={dibsCss.textNoir}>
            {originalPrice?.amount && (
                <span
                    className={classnames(
                        styles.originalPrice,
                        dibsCss.sassyFontBodySmall,
                        dibsCss.textSatan,
                        dibsCss.inlineBlock,
                        dibsCss.mrXxsmall
                    )}
                >
                    <FormattedPrice value={originalPrice.amount} currency={currency} />
                </span>
            )}

            <CurrentPrice
                currentPrice={currentPrice}
                quantityLabel={quantityLabel}
                currency={currency}
            />

            <CurrentPriceLabelAndPercentageOff
                type={currentPrice.type}
                percentageOff={percentageOff}
            />
        </span>
    );
};
